"use client";

import { api } from "@convex/_generated/api";
import { Authenticated, useQuery } from "convex/react";
import { Suspense, useEffect, useState } from "react";
import { UsernameModal } from "./username-modal";

interface UsernameCheckProviderProps {
  children: React.ReactNode;
}

export function UsernameCheckProvider({
  children,
}: UsernameCheckProviderProps) {
  const [showModal, setShowModal] = useState(false);
  const [hasChecked, setHasChecked] = useState(false);

  return (
    <>
      {children}
      {/* Auth-gated, request-time only; keep it out of the prerendered shell. */}
      <Suspense fallback={null}>
        <Authenticated>
          <UsernameChecker
            hasChecked={hasChecked}
            onChecked={() => setHasChecked(true)}
            onHideModal={() => setShowModal(false)}
            onShowModal={() => setShowModal(true)}
          />
          <UsernameModal
            onClose={() => {
              setShowModal(false);
              setHasChecked(true);
            }}
            open={showModal}
          />
        </Authenticated>
      </Suspense>
    </>
  );
}

interface UsernameCheckerProps {
  onShowModal: () => void;
  onHideModal: () => void;
  hasChecked: boolean;
  onChecked: () => void;
}

function UsernameChecker({
  onShowModal,
  onHideModal,
  hasChecked,
  onChecked,
}: UsernameCheckerProps) {
  const user = useQuery(api.users.queries.current);

  useEffect(() => {
    // Only check once when user data is loaded
    if (user && !hasChecked) {
      onChecked();

      // Show modal if user doesn't have a username
      if (!user.username) {
        onShowModal();
      }
    }
  }, [user, hasChecked, onShowModal, onChecked]);

  // Hide modal if user gets a username (e.g., from another tab)
  useEffect(() => {
    if (user?.username) {
      onHideModal();
    }
  }, [user?.username, onHideModal]);

  return null;
}
